<template>
  <div class="list-tools" :class="{ 'pointer-events-none': list == null }">
    <div
      class="slot slot-round"
      :class="{ 'slot-visible': expanded || quickActions.includes('edit') }"
      :style="{
        right: positions[0][0],
        bottom: positions[0][1]
      }"
      v-show="allowedActions.includes('edit')"
    >
      <button
        @click="callEdit"
        :style="{ 'transition-delay': positions[0][2] }"
      >
        <icon-round-edit :showNotification="showEditNotification" />
      </button>
    </div>

    <div
      class="slot slot-round"
      :class="{ 'slot-visible': expanded || quickActions.includes('copy') }"
      :style="{
        right: positions[1][0],
        bottom: positions[1][1]
      }"
      v-show="allowedActions.includes('copy')"
    >
      <button
        @click="callCopy"
        :style="{ 'transition-delay': positions[1][2] }"
      >
        <icon-copy />
      </button>
    </div>

    <div
      class="slot slot-round"
      :class="{ 'slot-visible': expanded || quickActions.includes('delete') }"
      :style="{
        right: positions[2][0],
        bottom: positions[2][1]
      }"
      v-show="allowedActions.includes('delete')"
    >
      <button
        @click="callDelete"
        :style="{ 'transition-delay': positions[2][2] }"
      >
        <icon-delete />
      </button>
    </div>

    <div
      class="slot slot-round"
      :class="{
        'slot-visible': expanded || quickActions.includes('showelectionview')
      }"
      :style="{
        right: positions[3][0],
        bottom: positions[3][1]
      }"
      v-show="allowedActions.includes('showelectionview')"
    >
      <button
        @click="callShow"
        :style="{ 'transition-delay': positions[3][2] }"
      >
        <icon-list-summary />
      </button>
    </div>
    <div
      class="slot slot-heart"
      :class="{ 'slot-visible': expanded || quickActions.includes('choose') }"
      :style="{
        right: positions[4][0],
        bottom: positions[4][1]
      }"
      v-show="allowedActions.includes('choose')"
    >
      <button
        @click="callChoose"
        :style="{ 'transition-delay': positions[4][2] }"
      >
        <icon-heart-filled v-if="list != null && list.id === choice" />
        <icon-heart-empty v-else />
      </button>
    </div>

    <div
      class="slot slot-visible slot-dots absolute bottom-0 right-0"
      v-show="allowedActions.length > quickActions.length"
    >
      <button @click="toggleExpanded"><icon-dots /></button>
    </div>
  </div>
</template>

<script>
import IconRoundEdit from "@/components/icons/IconRoundEdit";
import IconDelete from "@/components/icons/IconDelete";
import IconCopy from "@/components/icons/IconCopy";
import IconListSummary from "@/components/icons/IconListSummary";
import { mapGetters } from "vuex";
import IconDots from "@/components/icons/IconDots";
import IconHeartFilled from "@/components/icons/IconHeartFilled";
import IconHeartEmpty from "@/components/icons/IconHeartEmpty";

export default {
  name: "ListTools",
  components: {
    IconHeartEmpty,
    IconHeartFilled,
    IconDots,
    IconListSummary,
    IconCopy,
    IconDelete,
    IconRoundEdit
  },
  props: {
    list: {
      type: Object,
      default: null
    },
    /**
     * All actions available in the expanded state, possible values are
     * "copy", "edit", "delete", "choose", "showelectionview"
     */
    allowedActions: {
      type: Array,
      required: true
    },
    /**
     * All actions available in the collapsed state, possible values are
     * "copy", "edit", "delete", "choose", "showelectionview"
     */
    quickActions: {
      type: Array,
      default: () => []
    },
    alwaysExpanded: {
      type: Boolean,
      default: false
    },
    showEditNotification: {
      type: Boolean
    }
  },
  data: () => {
    return {
      expanded: false,
      positions: [
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0],
        [0, 0, 0]
      ]
    };
  },
  computed: {
    ...mapGetters("list", ["choice"])
  },
  mounted() {
    if (this.alwaysExpanded) {
      this.expanded = true;
    }
    this.updateLayout();
  },
  watch: {
    allowedActions: function() {
      this.updateLayout();
    },
    quickActions: function() {
      this.updateLayout();
    }
  },
  methods: {
    callShow() {
      this.$emit("showelectionview");
    },
    callChoose() {
      this.$emit("choose");
    },
    callCopy() {
      this.$emit("copy");
    },
    callEdit() {
      this.$emit("edit");
    },
    callDelete() {
      this.$emit("delete");
    },
    toggleExpanded() {
      this.expanded = !this.expanded;
      this.updateLayout();
      this.$emit("expand", this.expanded);
    },
    updateLayout() {
      const expandable = this.allowedActions.length > this.quickActions.length;
      const rightOffset = expandable ? 0.6 : 0;
      const positions = this.positions;
      const actions = ["edit", "copy", "delete", "showelectionview", "choose"];
      const referencePositions = [
        [0, 0],
        [1, 0],
        [2, 0],
        [1, 1],
        [2, 1]
      ];

      const currentActions = this.expanded
        ? this.allowedActions
        : this.quickActions;
      let currentActionIndex = 0;
      let delayIndex = 0;
      for (let i = 0; i < actions.length; i++) {
        const action = actions[i];
        if (!currentActions.includes(action)) {
          continue;
        }
        const position = referencePositions[currentActionIndex];
        positions[i][0] = position[0] + rightOffset + "em";
        positions[i][1] = position[1] + "em";
        positions[i][2] = delayIndex * 0.05 + "s";
        currentActionIndex++;
        if (!this.quickActions.includes(action)) {
          delayIndex++;
        }
      }

      this.positions = [...this.positions];
    }
  }
};
</script>

<style scoped>
.list-tools {
  font-size: 2.6rem;
}
.slot {
  position: absolute;
  bottom: 0;
  right: 0;
  width: 2.4375rem;
  height: 2.4375rem;
  transition: transform 0.1s ease-in-out;
}
.slot button {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  transition: transform 0.1s cubic-bezier(0.36, 0, 0.66, -0.56);
}
/deep/ .slot button .svg-icon {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
}
.slot:not(.slot-visible) {
  pointer-events: none;
}
.slot:not(.slot-visible) button {
  transform: scale(0);
}
.slot.slot-visible button {
  transform: scale(1);
  transition: transform 0.1s cubic-bezier(0.34, 1.56, 0.64, 1);
}

.slot-dots {
  width: 0.6em;
}

.slot-round button {
  font-size: 2.4375rem;
}

.slot-heart button {
  font-size: 1.65625rem;
}

.slot-dots button {
  font-size: 1.25rem;
}

@media screen and (min-width: 768px) {
  .list-tools {
    font-size: 3rem;
  }
  .slot,
  .slot button {
    width: 2.78125rem;
    height: 2.78125rem;
  }

  .slot-dots {
    width: 0.6em;
  }

  .slot-round button {
    font-size: 2.78125rem;
  }

  .slot-heart button {
    font-size: 1.875rem;
  }

  .slot-dots button {
    font-size: 1.40625rem;
  }
}
</style>
